import ExcelJS from 'exceljs';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { loadTurkishFont, TURKISH_FONT } from '../../shared/utils/pdfTurkishFont';
import { formatFinancialStatementAmount } from './financialPositionPresentation';

const EXCEL_COLORS = {
  title: 'FF16324F',
  header: 'FF16324F',
  summary: 'FFE1EAF5',
  border: 'FFD3DAE5',
  text: 'FF132238'
};

const PDF_COLORS = {
  title: [22, 50, 79],
  summary: [225, 234, 245],
  text: [19, 34, 56],
  border: [211, 218, 229]
};

function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

function buildWorksheetColumns(presentation) {
  return [
    { header: presentation.labels.sourceType, key: 'sourceType', width: 18 },
    { header: presentation.labels.sourceCode, key: 'sourceCode', width: 20 },
    { header: presentation.labels.sourceName, key: 'sourceName', width: 28 },
    { header: presentation.labels.vukAmount, key: 'vukAmount', width: 16 },
    { header: presentation.labels.ifrsAmount, key: 'ifrsAmount', width: 16 },
    { header: presentation.labels.ifrsEffect, key: 'ifrsEffect', width: 16 },
    { header: presentation.labels.standardRef, key: 'standardRef', width: 18 },
    { header: presentation.labels.explanation, key: 'explanation', width: 40 }
  ];
}

function buildAuditColumns(presentation) {
  return [
    { header: presentation.labels.sourceType, key: 'sourceType', width: 14 },
    { header: presentation.labels.sourceCode, key: 'sourceCode', width: 14 },
    { header: presentation.labels.sourceName, key: 'sourceName', width: 28 },
    { header: presentation.labels.vukAmount, key: 'vukAmount', width: 16 },
    { header: presentation.labels.ifrsAccount, key: 'ifrsAccount', width: 14 },
    { header: presentation.labels.ifrsAccountName, key: 'ifrsAccountName', width: 28 },
    { header: presentation.labels.mappingAmount, key: 'mappingAmount', width: 16 },
    { header: presentation.labels.recognitionAdjustment, key: 'recognitionAdjustment', width: 16 },
    { header: presentation.labels.measurementAdjustment, key: 'measurementAdjustment', width: 16 },
    { header: presentation.labels.ifrsEffect, key: 'ifrsEffect', width: 16 },
    { header: presentation.labels.standardRef, key: 'standardRef', width: 18 },
    { header: presentation.labels.explanation, key: 'explanation', width: 40 }
  ];
}

export async function exportVukIfrsComparisonToExcel({ presentation, filename, companyName }) {
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet('VUK_IFRS_Comparison', {
    views: [{ state: 'frozen', ySplit: 4 }]
  });

  const columns = presentation.comparisonVariant === 'worksheet'
    ? buildWorksheetColumns(presentation)
    : buildAuditColumns(presentation);
  const lastColumnLetter = String.fromCharCode(64 + columns.length);

  sheet.columns = columns.map((column) => ({ width: column.width }));

  sheet.mergeCells(`A1:${lastColumnLetter}1`);
  sheet.getCell('A1').value = presentation.labels.statementName;
  sheet.getCell('A1').font = { size: 14, bold: true, color: { argb: 'FFFFFFFF' } };
  sheet.getCell('A1').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: EXCEL_COLORS.title } };
  sheet.getCell('A1').alignment = { horizontal: 'center', vertical: 'middle' };

  sheet.mergeCells(`A2:${lastColumnLetter}2`);
  sheet.getCell('A2').value = companyName || '';
  sheet.getCell('A2').font = { size: 10, italic: true, color: { argb: EXCEL_COLORS.text } };
  sheet.getCell('A2').alignment = { horizontal: 'center' };

  sheet.addRow(columns.map((column) => column.header));

  const headerRow = sheet.getRow(3);
  for (let columnIndex = 1; columnIndex <= columns.length; columnIndex += 1) {
    const cell = headerRow.getCell(columnIndex);
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: EXCEL_COLORS.header } };
    cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
  }

  presentation.rows.forEach((row) => {
    const excelRow = sheet.addRow(columns.map((column) => row[column.key] ?? ''));
    excelRow.eachCell((cell, index) => {
      cell.border = {
        top: { style: 'thin', color: { argb: EXCEL_COLORS.border } },
        left: { style: 'thin', color: { argb: EXCEL_COLORS.border } },
        bottom: { style: 'thin', color: { argb: EXCEL_COLORS.border } },
        right: { style: 'thin', color: { argb: EXCEL_COLORS.border } }
      };
      const isNumericColumn = typeof row[columns[index - 1]?.key] === 'number';
      cell.alignment = { vertical: 'top', horizontal: isNumericColumn ? 'right' : 'left', wrapText: true };
      if (isNumericColumn) {
        cell.numFmt = '#,##0.00';
      }
    });
  });

  const summaryValues = presentation.comparisonVariant === 'worksheet'
    ? [
        '',
        '',
        presentation.labels.totalVukAmount,
        presentation.totals.totalVukAmount,
        presentation.totals.totalIfrsAmount,
        presentation.totals.totalIfrsEffect,
        '',
        ''
      ]
    : [
        '',
        '',
        presentation.labels.totalVukAmount,
        presentation.totals.totalVukAmount,
        '',
        '',
        presentation.totals.totalMappingAmount,
        presentation.totals.totalRecognitionAdjustment,
        presentation.totals.totalMeasurementAdjustment,
        presentation.totals.totalIfrsEffect,
        '',
        ''
      ];
  const summaryRow = sheet.addRow(summaryValues);
  summaryRow.eachCell((cell) => {
    cell.font = { bold: true, color: { argb: EXCEL_COLORS.text } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: EXCEL_COLORS.summary } };
  });

  const buffer = await workbook.xlsx.writeBuffer();
  triggerDownload(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), filename);
}

export async function exportVukIfrsComparisonToPdf({ presentation, filename, companyName }) {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a3' });
  await loadTurkishFont(doc);
  doc.setFont(TURKISH_FONT, 'normal');

  doc.setFontSize(15);
  doc.setTextColor(...PDF_COLORS.title);
  doc.text(presentation.labels.statementName, 210, 14, { align: 'center' });

  if (companyName) {
    doc.setFontSize(10);
    doc.setTextColor(...PDF_COLORS.text);
    doc.text(companyName, 210, 20, { align: 'center' });
  }

  const columns = presentation.comparisonVariant === 'worksheet'
    ? buildWorksheetColumns(presentation)
    : buildAuditColumns(presentation);

  autoTable(doc, {
    startY: companyName ? 26 : 20,
    head: [columns.map((column) => column.header)],
    body: presentation.rows.map((row) => (
      columns.map((column) => (
        typeof row[column.key] === 'number'
          ? formatFinancialStatementAmount(row[column.key], presentation.locale)
          : row[column.key]
      ))
    )),
    margin: { left: 8, right: 8 },
    theme: 'plain',
    styles: {
      font: TURKISH_FONT,
      fontSize: 7,
      textColor: PDF_COLORS.text,
      lineColor: PDF_COLORS.border,
      lineWidth: 0.1
    },
    headStyles: {
      fillColor: PDF_COLORS.title,
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      halign: 'center'
    }
  });

  doc.save(filename);
}
